import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api, ApiError } from '../../lib/api';

interface Connection {
  provider: string;
  handle: string | null;
  connected_at: string;
}

interface Connections {
  connections: Connection[];
  available: Record<string, boolean>;
  has_password: boolean;
}

/**
 * Accounts connected to this one.
 *
 * ## Connecting is a navigation, not a request
 *
 * The button asks the API where to go and then walks the whole page there,
 * because the provider's authorization screen is a website and not something a
 * `fetch` can complete. Which means the answer arrives as a query parameter on
 * the way back rather than as a response, and the notice below is how that gets
 * said.
 *
 * ## Disconnecting is refused when it would lock somebody out
 *
 * An account that arrived through Discord has no password. Removing its only
 * connection would leave nothing to sign in with, so the button is not offered
 * and the reason is written next to where it would have been. The server
 * refuses it too - this is the explanation, not the enforcement.
 */
export function Connections() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { data } = useQuery({
    queryKey: ['me', 'connections'],
    queryFn: () => api.get<Connections>('/me/connections'),
  });

  /*
   * Read once and taken out of the address bar, so the notice does not come
   * back every time somebody returns to this page from their history.
   */
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    const connection = new URLSearchParams(search).get('connection');

    if (connection === null) return;

    setOutcome(connection);
    navigate('/settings', { replace: true });
  }, [search, navigate]);

  const connect = useMutation({
    mutationFn: () => api.post<{ url: string }>('/me/connections/discord'),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const disconnect = useMutation({
    mutationFn: () => api.delete('/me/connections/discord'),
    onSuccess: () => client.invalidateQueries({ queryKey: ['me', 'connections'] }),
  });

  if (data === undefined || data.available.discord !== true) return null;

  const discord = data.connections.find((row) => row.provider === 'discord') ?? null;

  return (
    <Card className="p-6" data-connections>
      <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-faint">Connected accounts</h2>

      {outcome !== null && (
        <div className="mb-4">
          <Alert tone={outcome === 'discord' ? 'success' : 'error'}>{NOTICES[outcome] ?? NOTICES.failed}</Alert>
        </div>
      )}

      {disconnect.error instanceof ApiError && (
        <div className="mb-4">
          <Alert tone="error">{disconnect.error.message}</Alert>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-ink">Discord</p>

          <p className="mt-0.5 text-[13px] text-soft" data-connection-state={discord === null ? 'off' : 'on'}>
            {discord === null
              ? 'Sign in with one click instead of a password.'
              : `Connected as ${discord.handle ?? 'your Discord account'}.`}
          </p>

          {discord !== null && !data.has_password && (
            <p className="mt-1 text-[13px] text-faint">
              This is the only way into your account. Set a password from the sign-in page to be able to remove it.
            </p>
          )}
        </div>

        {discord === null ? (
          <Button variant="secondary" loading={connect.isPending} onClick={() => connect.mutate()}>
            Connect
          </Button>
        ) : (
          data.has_password && (
            <Button variant="ghost" loading={disconnect.isPending} onClick={() => disconnect.mutate()}>
              Disconnect
            </Button>
          )
        )}
      </div>
    </Card>
  );
}

/**
 * What the callback's one-word answer means in English.
 *
 * "Taken" is deliberately not specific about which of the two refusals it was.
 * Saying that the Discord account belongs to somebody else here would turn this
 * page into a way to ask whether a given Discord has an account, one guess at a
 * time.
 */
const NOTICES: Record<string, string> = {
  discord: 'Discord connected. You can sign in with it from now on.',
  taken: 'That Discord account is not available to connect here.',
  cancelled: 'Nothing was connected.',
  failed: 'That did not go through. Try again in a moment.',
};
